import { useContext, useState, useEffect } from 'react';
import { DownloadIcon } from '@heroicons/react/solid';
import { analytics } from "../../../helpers/analytics";
import { trackDownload } from "../../../helpers/metaPixel";
import { Asset } from 'contentful';

import { UserContext } from '../../../context/userContext';
import { StarIcon, StrokeIcon } from '../../../Icons';
import { Button } from '../../Button';
import { ButtonLink } from '../../ButtonLink';
import { canShare, isIos } from '../../../helpers/shared';
import { Notification } from '../../Notification';
import { Spinner } from '../../Spinner';
import { ISectionInstall } from '../../../@types/generated/contentful';
import { getContentfulImageUrl, getSafeImageUrl } from '../../../helpers/image';

type Props = {
  brush: ISectionInstall;
  image?: Asset;
  setTitle?: string;
  isLastItem?: boolean;
  onDownload: () => void;
};

const allowedFormats = {
  swatches: 'application/zip',
  ttf: 'font/ttf',
  png: 'image/png',
  usdz: 'application/zip',
  brush: 'application/zip',
  brushset: 'application/zip',
};

const ToolsListItem = ({ brush, image, isLastItem, setTitle, onDownload }: Props) => {
  const [isArSupported, setArSupported] = useState(false);
  const [isWarningVisible, setWarningVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { user } = useContext(UserContext);
  const { email, hasPremium = false } = user || {};
  const canDownload = Boolean(email && hasPremium);

  const getZip = async (href: string) => {
    const response = await fetch(href);
    const buffer = await response.arrayBuffer();

    return buffer;
  };

  const matchFormats = /(brush|brushset|png|swatches|ttf|usdz)$/;
  const matches = brush.fields.installurl.match(matchFormats);
  const fileFormat = matches ? matches[0] : 'zip';
  const titleFormat = (title: string) => title.replace(/\s/g, '_');
  const fallbackImage = getContentfulImageUrl(image, { width: 128, height: 128, fit: 'thumb' });
  const previewImage = getSafeImageUrl(brush.fields.logo) || fallbackImage;

  const sendToMixpanel = () => {
    analytics.track('Item Selected', {
      premium: hasPremium,
      set: setTitle,
      item: brush.fields.title,
      price: brush.fields.price,
      file_format: fileFormat,
      can_download: canDownload,
    });
  };

  const sendDownloadEvent = () => {
    analytics.track('Download Started', {
      premium: hasPremium,
      set: setTitle,
      item: brush.fields.title,
      price: brush.fields.price,
      file_format: fileFormat,
      delivery: canShare() && isIos() ? "native_share" : "direct_link",
    });
    trackDownload({
      premium: hasPremium,
      content_name: brush.fields.title,
      content_category: setTitle,
      price: brush.fields.price,
    });
  };

  const handleButtonClick = () => {
    sendToMixpanel();
    if (!canDownload) {
      analytics.track("Download Blocked", {
        reason: email ? "premium_required" : "auth_required",
        premium: hasPremium,
        set: setTitle,
        item: brush.fields.title,
        price: brush.fields.price,
        file_format: fileFormat,
      });
      onDownload();
      return;
    }

    setIsLoading(true);
    sendDownloadEvent();

    getZip(brush.fields.installurl).then((r) => {
      const file = new File([r], `${titleFormat(brush.fields.title)}.${fileFormat}`, {
        type: allowedFormats[fileFormat as keyof typeof allowedFormats] || 'application/octet-stream',
      });
      if (navigator.share && file) {
        try {
          analytics.track("Native Share Download Opened", {
            set: setTitle,
            item: brush.fields.title,
            file_format: fileFormat,
          });
          navigator
            .share({
              files: [file],
            })
            .then(() => {
              analytics.track("Download Completed", {
                set: setTitle,
                item: brush.fields.title,
                file_format: fileFormat,
                delivery: "native_share",
              });
            })
            .catch((error) => {
              analytics.track("Download Failed", {
                set: setTitle,
                item: brush.fields.title,
                file_format: fileFormat,
                delivery: "native_share",
                error_message:
                  error instanceof Error ? error.message : String(error),
              });
            })
            .finally(() => setIsLoading(false));
        } catch (err) {
          setIsLoading(false);
          analytics.track("Download Failed", {
            set: setTitle,
            item: brush.fields.title,
            file_format: fileFormat,
            delivery: "native_share",
            error_message: err instanceof Error ? err.message : String(err),
          });
          // eslint-disable-next-line no-console
          console.log(err);
        }
      }
    }).catch((error) => {
      setIsLoading(false);
      analytics.track("Download Failed", {
        set: setTitle,
        item: brush.fields.title,
        file_format: fileFormat,
        delivery: "native_share",
        error_message: error instanceof Error ? error.message : String(error),
      });
    });
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    sendToMixpanel();
    if (!canDownload) {
      e.preventDefault();
      analytics.track("Download Blocked", {
        reason: email ? "premium_required" : "auth_required",
        premium: hasPremium,
        set: setTitle,
        item: brush.fields.title,
        price: brush.fields.price,
        file_format: fileFormat,
      });
      onDownload();
      return;
    }

    setTimeout(async () => {
      // @ts-ignore
      gtag('event', 'file_download', {
        'transaction_id': Math.random() * 100000,
      });
    }, 100);
    sendDownloadEvent();
    analytics.track("Download Completed", {
      set: setTitle,
      item: brush.fields.title,
      file_format: fileFormat,
      delivery: "direct_link",
    });

    if (isIos() && !canShare()) {
      analytics.track("IOS Install Warning Shown", {
        source: "download",
        set: setTitle,
        item: brush.fields.title,
      });
      setWarningVisible(true);
    }
  };

  useEffect(() => {
    const a = document.createElement('a');
    if (a.relList.supports('ar')) {
      setArSupported(true);
    }
  }, []);

  useEffect(() => {
    if (isWarningVisible) {
      const timer = setTimeout(() => setWarningVisible(false), 4000);
      return () => clearTimeout(timer);
    }
    return () => {};
  });

  return (
    <div className='flex justify-between items-center py-4 px-6 relative xs:px-4'>
      <div className='flex items-center'>
        <span className='inline-block relative shrink-0 rounded-full' style={{ backgroundColor: brush.fields.color }}>
          {previewImage && (
            <img
              className='h-16 w-16 rounded-full object-cover'
              src={previewImage}
              alt=''
              loading='lazy'
              decoding='async'
            />
          )}

          {brush.fields.price === 'Premium' && (
            <span
              className={`absolute 
      bottom-0 right-0 flex justify-center items-center p-[3px] rounded-full bg-main-blue text-white`}
            >
              <StarIcon className='w-[13px] h-[13px]' />
            </span>
          )}
        </span>
        <div className='text-left ml-5'>
          <p className='text-lg leading-lg font-bold mb-[2px]'>{brush.fields.title}</p>
          <p className='text-sm leading-sm'>{brush.fields.description}</p>
          {fileFormat === 'usdz' && isArSupported && canDownload && (
            // eslint-disable-next-line react/no-invalid-html-attribute
            <a className='text-sm leading-sm text-main-blue' href={brush.fields.installurl} rel='ar'>
              {previewImage && <img alt='model preview' src={previewImage} className='hidden' />}
              Show in AR
            </a>
          )}
          {fileFormat === 'usdz' && isArSupported && !canDownload && (
            <button className='text-sm leading-sm text-main-blue' type='button' onClick={onDownload}>
              Show in AR
            </button>
          )}
        </div>
      </div>
      {/* @ts-ignore */}
      {canShare() && isIos() ? (
        <>
          <div className='w-[183px] ml-4 shrink-0 xs:hidden'>
            <Button theme='blue' size='small' onClick={handleButtonClick}>
              {isLoading ? <Spinner theme='blue' /> : <span>Download</span>}
            </Button>
          </div>
          <div className='w-[40px] ml-4 shrink-0 hidden xs:block'>
            <Button theme='blue' size='small' onClick={handleButtonClick}>
              {isLoading ? <Spinner theme='blue' /> : <DownloadIcon className='w-4 h-4' />}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className='w-[183px] ml-4 shrink-0 xs:hidden'>
            {canDownload ? (
              <ButtonLink theme='blue' size='small' href={brush.fields.installurl} onClick={handleLinkClick} download>
                Download
              </ButtonLink>
            ) : (
              <Button theme='blue' size='small' onClick={handleButtonClick}>
                <span>Download</span>
              </Button>
            )}
          </div>
          <div className='w-[40px] ml-4 shrink-0 hidden xs:block'>
            {canDownload ? (
              <ButtonLink theme='blue' size='small' onClick={handleLinkClick} href={brush.fields.installurl} download>
                <DownloadIcon className='w-4 h-4' />
              </ButtonLink>
            ) : (
              <Button theme='blue' size='small' onClick={handleButtonClick}>
                <DownloadIcon className='w-4 h-4' />
              </Button>
            )}
          </div>
        </>
      )}
      {!isLastItem && (
        <div
          className={`left-[72px] 
        right-[32px] bottom-0 h-[1px] bg-main-grey-400 absolute xs:left-[16px] xs:right-[16px]`}
        />
      )}
      {isWarningVisible && (
        <Notification
          icon={<StrokeIcon className='w-[20px]' />}
          text='Content install is working only on iOS 15 and newer'
        />
      )}
    </div>
  );
};

export { ToolsListItem };
